import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, X, FileText, Briefcase, Building2, BookOpen } from "lucide-react";
import { API } from "@/lib/constants";

const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [searchData, setSearchData] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      window.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }
    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!query.trim()) {
      setSearchData(null);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
        setSearchData(response.data);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [query]);

  const handleResultClick = (type, slug, url) => {
    onClose();
    setQuery("");
    setSearchData(null);
    
    // Use the full URL if available, otherwise construct from type + slug
    if (url) {
      navigate(url);
    } else if (type === "service") navigate(`/services/${slug}`);
    else if (type === "blog") navigate(`/blog/${slug}`);
    else if (type === "case_study") navigate(`/case-studies/${slug}`);
    else if (type === "industry") navigate(`/industries/${slug}`);
  };

  if (!isOpen) return null;

  // Extract results safely
  const totalResults = searchData?.total_results || 0;
  const services = searchData?.results?.services || [];
  const blogs = searchData?.results?.blog || [];
  const caseStudies = searchData?.results?.case_studies || [];
  const hasResults = totalResults > 0;
  const noResults = searchData && totalResults === 0;

  return (
    <div className="fixed inset-0 z-50" data-testid="search-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative flex items-start justify-center pt-[15vh]">
        <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl w-full max-w-2xl mx-4 overflow-hidden">
          <div className="flex items-center gap-3 p-4 border-b border-white/10">
            <Search className="w-5 h-5 text-gray-500" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search services, blog posts..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 bg-transparent border-0 text-lg text-white outline-none placeholder:text-gray-500"
              data-testid="search-input"
            />
            {query && (
              <button onClick={() => { setQuery(""); setSearchData(null); }} className="p-1 text-gray-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto p-4">
            {loading && (
              <div className="text-center py-8">
                <div className="text-[#00E676] animate-pulse">Searching...</div>
              </div>
            )}

            {noResults && (
              <div className="text-center py-8">
                <p className="text-gray-400">No results found</p>
              </div>
            )}

            {!loading && !searchData && (
              <div className="text-center py-8">
                <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Start typing to search</p>
              </div>
            )}

            {hasResults && (
              <div className="space-y-4">
                <p className="text-sm text-gray-500">Found {totalResults} results</p>
                
                {services.length > 0 && (
                  <ResultSection title="Services" items={services} icon={Briefcase} onItemClick={handleResultClick} />
                )}

                {blogs.length > 0 && (
                  <ResultSection title="Blog" items={blogs} icon={FileText} onItemClick={handleResultClick} />
                )}

                {caseStudies.length > 0 && (
                  <ResultSection title="Case Studies" items={caseStudies} icon={BookOpen} onItemClick={handleResultClick} />
                )}
              </div>
            )}
          </div>

          <div className="p-4 border-t border-white/10 flex justify-between text-xs text-gray-500">
            <span>Press ESC to close</span>
            <span>Digital Media Ninja</span>
          </div>
        </div>
      </div>
    </div>
  );
};

function ResultSection({ title, items, icon: Icon, onItemClick }) {
  return (
    <div>
      <h3 className="text-sm font-medium text-gray-400 mb-2">{title}</h3>
      {items.map((item, i) => (
        <button
          key={i}
          onClick={() => onItemClick(item.type, item.slug, item.url)}
          className="w-full p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left flex items-start gap-3 mb-2"
        >
          <Icon className="w-4 h-4 text-[#00E676] mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-white truncate">{item.title}</p>
            <p className="text-sm text-gray-400 truncate">{item.excerpt}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

export default SearchModal;
